/**
 * Token-only styles for form controls Chakra would otherwise paint with raw
 * palette steps (docs/design/design-system.md, ux-principles.md):
 *  - an unchecked control is bounded by `border.strong` (>= 3:1, WCAG 1.4.11),
 *    never the 1.23:1 default gray;
 *  - a checked control fills with `brand.accent` (non-text, >= 3:1);
 *  - keyboard focus is a 2px `brand.accent` outline with a 2px offset;
 *  - the thumb / radio ground / radio dot use `bg.default` (--ips-bg), never
 *    `bg.surface`: --ips-surface is translucent in Expressive dark (2.09-2.79:1
 *    against the track there), while --ips-bg is opaque in all four
 *    combinations (gate-asserted) and equals --ips-surface in Calm.
 */

/** Minimum touch target (WCAG 2.5.5, design-system.md). In px on purpose: rem
 *  shrinks with it, main.css sets a 15px root at <= 768px, where 2.75rem is 41px. */
export const TOUCH_TARGET = "44px";

export const FOCUS_RING = {
  outline: "2px solid",
  outlineColor: "brand.accent",
  outlineOffset: "2px",
  boxShadow: "none",
} as const;

export const switchSx = {
  ".chakra-switch__track": { bg: "border.strong" },
  ".chakra-switch__track[data-checked]": { bg: "brand.accent" },
  ".chakra-switch__track[data-focus-visible]": FOCUS_RING,
  ".chakra-switch__thumb": { bg: "bg.default" },
} as const;

/**
 * Chakra's <Radio> routes `sx` to the control span itself (getCheckboxProps),
 * not to the root label — unlike <Switch>, whose `sx` lands on the root. So
 * these selectors target the element (`&`), not a `.chakra-radio__control`
 * descendant: the descendant form matched nothing and every radio kept
 * Chakra's 1.23:1 default ring (found in a browser 2026-10-31).
 */
export const radioSx = {
  borderColor: "border.strong",
  bg: "bg.default",
  "&[data-checked]": {
    bg: "brand.accent",
    borderColor: "brand.accent",
    color: "bg.default",
  },
  "&[data-focus-visible]": FOCUS_RING,
} as const;
